import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useMousePositionAsTrigger } from 'react-laag';
import Button from '../../../components/button';
import FormSelect from '../../../components/dropdown/formSelect';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import PopoverModalComponent from '../../../components/modal/popoverModal';
import Search from '../../../components/search/search';
import Table from '../../../components/table';
import Heading from '../../../components/typography';
import InitialValuesForm from '../../../constants/initialValues';
import { COLORS } from '../../../constants/style/color';
import { SystemUserHeader } from '../../../constants/table-headers/systemUserHeader';
import {
  DEFAULT_TENANT_SCOPE,
  INSTANCE_TENANT_SCOPE,
  resolveTenantScope,
  type TenantScope,
} from '../../../constants/tenantScope';
import {
  useAdvanceBoolean,
  useBoolean,
  useGetAllCustomerUserList,
  useGetCustomerCompanyName,
  useGetSystemUserStatusList,
  useGetTablePreferences,
  usePostTablePreferences,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import {
  CustomerRoleService,
  CustomerUserService,
} from '@wealthlane/api-services/wealthlane-customer-service';
import { type ModalStatusType, type TableParamsType } from '../../../types/common';
import RolesPopoverContent from '../system-roles/rolesPopoverContent';
import {
  SettingSystemUserButton,
  SettingSystemUserHeader,
  SystemUserContainer,
} from './systemUserStyled';
import UserModel from './userModel';
import InstanceUserRoleModal, { instanceUserRoleInitialValues } from './instanceUserRoleModal';
import InstanceScopeBanner from '../InstanceScopeBanner';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import { useQueryClient } from 'react-query';
import { UserStatusOptions } from '../../../constants/common';
import Filter from '../../../components/filter';
import { ResetFilterWrapper } from '../../../constants/style/common.styled';
import DeactivatePopup from './deactivateModal';
import AlertPopup from '../../../components/alert-popup';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import Spinners from '../../../components/spinner';
import { axiosInstance } from '../../../services/apiInterceptor';
import { getUserName } from '../../../utils/global';

export const usersInitialValues = {
  data: InitialValuesForm.addSystemUserForm,
  id: '',
};
export const remarksInitialValues = {
  remarks: '',
  id: '',
};

export interface SystemUsersProps {
  scope?: TenantScope;
}

const SystemUsers = ({ scope = DEFAULT_TENANT_SCOPE }: SystemUsersProps): JSX.Element => {
  const tenantScope = resolveTenantScope(scope);
  const isInstanceScope = tenantScope !== DEFAULT_TENANT_SCOPE;
  const pageTitle = isInstanceScope ? 'Instance Users' : 'System Users';
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    customValue,
    setCustomValue,
  } = useAdvanceBoolean<string>(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const [usersInitialData, setUsersInitialData] = useState(usersInitialValues);
  const [instanceUserInitialData, setInstanceUserInitialData] = useState(
    instanceUserRoleInitialValues
  );
  const [instanceModalLoading, setInstanceModalLoading] = useState(false);
  // const [tableInfo, setTableInfo] = useState<TableParamsType>(InitialValuesForm.tableParams);
  const { tableInfo, setTableInfo, registerPermissionHandler } = useDynamicTable();
  const [headerList, setHeaderList] = useState<string[]>([]);

  useEffect(() => {
    setTableInfo({ ...InitialValuesForm.tableParams, scope: tenantScope });
  }, [tenantScope, setTableInfo]);
  useEffect(() => {
    registerPermissionHandler(() => (id: string, event: any) => {
      // console.log('Triggered handleShow with ID:', id);
      // console.log('popover info', popoverInfo);
      if (id) {
        getRolesData(id);
      } else {
        setAllPermission([]);
      }
      if (event) {
        handleMouseEvent(event);
      }
    });
  }, [registerPermissionHandler, tenantScope]);
  const [status, setStatus] = useState<number[]>([]);
  const [allPermission, setAllPermission] = useState({});
  const { isLoading, handleRequest } = useWealthlaneCustomerApi();
  const userAuthList = useSelector(selectAuthRoutesList);

  // const [checkedValues, setCheckedValues] = useState<any>();
  const [remark, setRemark] = useState<string>();
  const [userStatusRow, setUserStatusRow] = useState<boolean>();

  const { data: companyName } = useGetCustomerCompanyName(handleRequest);
  const { data: getCustomerUserCustomerUserList, status: load } = useGetAllCustomerUserList(
    handleRequest,
    tableInfo,
    tenantScope
  );
  const { data } = useGetSystemUserStatusList(handleRequest);

  const [dataRow, setDataRow] = useState<any>();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [isEditForm, setIsEditForm] = useState<boolean>(false);
  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'SYSTEMS_USERS_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });

  const { data: tablePreferences } = useGetTablePreferences(handleRequest, 'System Users');

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'System Users',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };

  const actions = [
    {
      id: 'view',
      action: 'View',
      onClick: async (id: string, rowData: any) => {
        setModalStatus('View');
        if (isInstanceScope) {
          await getInstanceUserData(id, rowData?.isDefaultAdmin);
          openModal();
          return;
        }
        setDataRow(rowData.status);
        setUserStatusRow(rowData.isEnabled);
        getUserData(id, rowData?.isDefaultAdmin);
        setUserStatusRow(rowData.isEnabled);
        setIsEditForm(true);
        openModal();
      },
    },
    {
      id: 'edit',
      action: 'Edit',
      as: 'Edit',
      hasId: 'users',
      onClick: async (id: string, rowData: any) => {
        setModalStatus('Edit');
        if (isInstanceScope) {
          await getInstanceUserData(id, rowData?.isDefaultAdmin);
          openModal();
          return;
        }
        setDataRow(rowData.status);
        await getUserDataForUpdate(id, rowData?.isDefaultAdmin);
        setIsEditForm(true);
        openModal();
      },
    },
    {
      id: 'approve',
      action: 'Approve',
      as: 'Approval',
      onClick: (id: string) => {
        setModalStatus('Approval');
        setCustomValue(id);
        openConfirmModal();
      },
    },
    {
      id: 'deactivate',
      action: <div className='delete-label'>Deactivate</div>,
      as: 'Deactivate',
      onClick: (id: string) => {
        setModalStatus('Deactivate');
        setCustomValue(id);
        openConfirmModal();
      },
    },
    {
      id: 'activate',
      action: 'Activate',
      as: 'Edit',
      onClick: (id: string) => {
        setModalStatus('Activate');
        setCustomValue(id);
        openConfirmModal();
      },
    },
    {
      id: 'reject',
      action: 'Reject',
      as: 'Approval',
      onClick: (id: string) => {
        setModalStatus('Reject');
        setCustomValue(id);
        openConfirmModal();
      },
    },
    {
      id: 'viewComments',
      action: 'View Comments',
      onClick: (id: string, rowData: any) => {
        setModalStatus('View Remarks');
        setDataRow(rowData);
        setCustomValue(id);
        getRemarksData(id, rowData);
        openConfirmModal();
      },
    },
    {
      id: 'resendEmail',
      action: 'Resend Email',
      onClick: (id: string) => {
        handleResendMail(id);
      },
    },
  ];

  const tableActions = isInstanceScope
    ? actions.filter((item) => item.id === 'view' || item.id === 'edit')
    : actions;

  const getInstanceUserData = async (id: string, isDefaultAdmin?: boolean): Promise<void> => {
    setInstanceModalLoading(true);
    try {
      const [instanceData, companyData] = await Promise.all([
        handleRequest(
          CustomerUserService.getApiCustomerApiAppCustomerUserCustomerUserForUpdateById(
            id,
            INSTANCE_TENANT_SCOPE
          )
        ),
        handleRequest(
          CustomerUserService.getApiCustomerApiAppCustomerUserCustomerUserForUpdateById(
            id,
            DEFAULT_TENANT_SCOPE
          )
        ),
      ]);

      if (instanceData != null || companyData != null) {
        const nameSource = {
          firstName: instanceData?.firstName ?? companyData?.firstName,
          middleName: instanceData?.middleName ?? companyData?.middleName,
          lastName: instanceData?.lastName ?? companyData?.lastName,
        };
        setInstanceUserInitialData({
          id,
          data: {
            fullName: getUserName(nameSource),
            employeeId: instanceData?.employeeId ?? companyData?.employeeId ?? '',
            companyRole: companyData?.customerRole ?? '',
            customerRoleId: instanceData?.customerRoleId ?? null,
            isDefaultAdmin: isDefaultAdmin ?? false,
          },
        });
      }
    } finally {
      setInstanceModalLoading(false);
    }
  };

  const onApprovePrimaryClick = (values: Record<string, any>): void => {
    getUserData(customValue, modalStatus, values);
  };

  const getUserData = (id: string, status?: string, values?: any): void => {
    if (status === 'Deactivate') {
      setLoading(true);
      handleRequest(
        CustomerUserService.postApiCustomerApiAppCustomerUserDeactivateCustomerUser({
          customerUserId: id,
          deactivationNote: remark as string,
        })
      )
        .then((response: any) => {
          if (response != null) {
            toast.success(<ToastComponent label={'Success'} message={'User Deactivated'} />);
            void queryClient.invalidateQueries('getAllCustomerUserList');
            closeConfirmModal();
            // setCheckedValues([]);
            // setLoading(false);
          }
        })
        .catch((error) => {
          console.log(error);
          // setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (status === 'Activate') {
      setLoading(true);
      handleRequest(
        CustomerUserService.postApiCustomerApiAppCustomerUserReActivateCustomerUser({
          customerUserId: id,
        })
      )
        .then((response: any) => {
          if (response != null) {
            toast.success(<ToastComponent label={'Success'} message={'User Activated'} />);
            void queryClient.invalidateQueries('getAllCustomerUserList');
            closeConfirmModal();
            // setCheckedValues([]);
            // setLoading(false);
          }
        })
        .catch((error) => {
          console.log(error);
          // setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      void handleRequest(
        CustomerUserService.getApiCustomerApiAppCustomerUserCustomerUserById(id)
      ).then((data: any) => {
        if (data != null) {
          const filteredData = {
            ...data,
            isDefaultAdmin: status,
          };

          setUsersInitialData({ id, data: filteredData });
        }
      });
    }
  };

  const handleResendMail = (id: string): void => {
    handleRequest(
      CustomerUserService.postApiCustomerApiAppCustomerUserResendEmailToCustomerUser(id)
    ).then((data: any) => {
      if (data) {
        toast.success(<ToastComponent label={'Success'} message={'Email resend'} />);
      }
    });
  };

  const getUserDataForUpdate = async (
    id: string,
    status?: boolean,
    values?: any
  ): Promise<void> => {
    await handleRequest(
      CustomerUserService.getApiCustomerApiAppCustomerUserCustomerUserForUpdateById(id, tenantScope)
    ).then((data: any) => {
      if (data != null) {
        const filteredData = {
          ...data,
          isDefaultAdmin: status,
        };

        setUsersInitialData({ id, data: filteredData });
      }
    });
  };
  // };

  const getRemarksData = (id: string, rowdata: any): void => {
    if (rowdata?.isEnabled === false) {
      void handleRequest(
        CustomerUserService.getApiCustomerApiAppCustomerUserDeactivationRemarks(id)
      ).then((data: string | undefined) => {
        if (data != null) {
          setRemark(data);

          // setRemarksInitialData({ id, remarks: data });
        }
      });
    } else {
      void handleRequest(CustomerUserService.getApiCustomerApiAppCustomerUserRemarks(id)).then(
        (data: string | undefined) => {
          if (data != null) {
            setRemark(data);

            // setRemarksInitialData({ id, remarks: data });
          }
        }
      );
    }
  };

  // To get System Roles Data
  const {
    hasMousePosition, // did we get a mouse-position from the event-handler
    resetMousePosition, // reset the mouse-position to `null`, essentially closing the menu
    handleMouseEvent, // event-handler we will use below
    trigger, // information regarding positioning we can provide to `useLayer`
  } = useMousePositionAsTrigger();

  const getRolesData = (id: string): void => {
    setLoading(true);
    handleRequest(
      CustomerRoleService.getApiCustomerApiAppCustomerRoleCustomerRoleByCustomerRoleId(
        id,
        tenantScope
      )
    )
      .then((data: any) => {
        if (data != null) {
          const filterData = data.permissions.reduce((a: any, v: any) => ({ ...a, [v]: true }), {});
          setAllPermission(filterData);
          setLoading(false);
        }
      })
      .catch((error: any) => {
        console.log('error', error);
        setLoading(false);
      });
  };

  const handleShow = (id: string, e: any): void => {
    if (id) {
      getRolesData(id);
    } else {
      setAllPermission([]);
    }
    handleMouseEvent(e);
  };
  const [controls, setControls] = React.useState({
    isEnabled: {
      value: 'true',
    },
  });
  const renderRightToolbarButton = () => {
    if (isInstanceScope) {
      return null;
    }

    return (
      <Button
        id='addUserButton'
        as='Create'
        hasId='users'
        variant={'primary'}
        title={'Add user'}
        icon={'add_circle_outline'}
        onClick={() => {
          setUsersInitialData(usersInitialValues);
          setModalStatus('Add');
          setIsEditForm(false);
          setDataRow('');
          openModal();
        }}
      />
    );
  };
  const tableHeaderList = SystemUserHeader({
    actions: schemaData?.columns,
    tableActions,
    hasMousePosition,
    resetMousePosition,
  })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      // console.log('item', item);

      return {
        label: item.Header,
        value: item.accessor,
      };
    });

  const sustemUsersHeader = (): any[] => {
    return SystemUserHeader({
      actions: schemaData?.columns,
      tableActions,
      hasMousePosition,
      resetMousePosition,
    }).filter((item) => item.isSubHeader === false || headerList.includes(item.accessor));
  };

  const schema = React.useMemo(
    () => ({
      columns: sustemUsersHeader(),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );
  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  return (
    <SystemUserContainer id='systemUsersPageContainer'>
      <SettingSystemUserHeader id='systemUsersPageHeader'>
        <Heading variant='h3' title={pageTitle} id='systemUsersPageTitle' />
        <Heading
          variant='body-s'
          title={`${String(companyName ?? '')} users with access to the Wealthlane system.`}
          color={`${COLORS.Gray}`}
          id='systemUsersPageDescription'
        />
      </SettingSystemUserHeader>
      {isInstanceScope && (
        <InstanceScopeBanner semanticId='instanceUsersScopeBanner' className='mb-4' />
      )}
      <div className='grid xl:grid-cols-12 items-start gap-2' id='systemUsersContentArea'>
        {isConfirmOpen &&
          modalStatus !== 'Deactivate' &&
          dataRow?.status &&
          dataRow?.status === 'Rejected' && (
            <AlertPopup
              isShow={isConfirmOpen}
              closeModal={closeConfirmModal}
              modalStatus={modalStatus}
              title={'this System User'}
              handleSubmit={onApprovePrimaryClick}
              setRemark={setRemark}
              remark={remark}
              loading={loading}
            />
          )}
        {isConfirmOpen && modalStatus === 'Activate' && (
          <AlertPopup
            isShow={isConfirmOpen}
            closeModal={closeConfirmModal}
            modalStatus={modalStatus}
            // title={'this System User'}
            customTitle={'Are you sure you want to activate this System User?'}
            handleSubmit={onApprovePrimaryClick}
            setRemark={setRemark}
            remark={remark}
            loading={loading}
          />
        )}
        {isConfirmOpen && (dataRow?.isEnabled === false || modalStatus === 'Deactivate') && (
          <DeactivatePopup
            isShow={isConfirmOpen}
            closeModal={closeConfirmModal}
            modalStatus={modalStatus}
            title='user'
            handleSubmit={onApprovePrimaryClick}
            setRemark={setRemark}
            remark={remark}
            loading={loading}
            dataRow={dataRow}
          />
        )}
        <AnimationWrapper>
          {isModalOpen && isInstanceScope && (
            <InstanceUserRoleModal
              isModalOpen={isModalOpen}
              closeModal={() => {
                setInstanceUserInitialData(instanceUserRoleInitialValues);
                closeModal();
              }}
              modalStatus={modalStatus}
              setModalStatus={setModalStatus}
              usersInitialData={instanceUserInitialData}
              loading={instanceModalLoading}
              scope={tenantScope}
            />
          )}
          {isModalOpen && !isInstanceScope && usersInitialData && (
            <UserModel
              isModalOpen={isModalOpen}
              isEditForm={isEditForm}
              closeModal={() => {
                setUsersInitialData(null);
                closeModal();
              }}
              modalStatus={modalStatus}
              setModalStatus={setModalStatus}
              usersInitialData={usersInitialData}
              isLoading={isLoading}
              tableInfo={tableInfo}
              setTableInfo={setTableInfo}
              dataRow={dataRow}
              userStatusRow={userStatusRow}
              scope={tenantScope}
            />
          )}
        </AnimationWrapper>
      </div>
      <PopoverModalComponent
        children={<RolesPopoverContent permissionData={allPermission} loading={loading} />}
        hasMousePosition={hasMousePosition}
        trigger={trigger}
        resetMousePosition={resetMousePosition}
        popoverClassName='roles-popovers'
      />

      {/* <Table
        columns={SystemUserHeader(actions, handleShow, hasMousePosition, resetMousePosition)}
        data={getCustomerUserCustomerUserList?.items ?? []}
        loading={load}
        showPagination
        totalCounts={getCustomerUserCustomerUserList?.totalCount ?? 10}
        tableInfo={tableInfo}
        setTableInfo={setTableInfo}
      /> */}
      <DynamicTable
        schema={schema}
        data={getCustomerUserCustomerUserList?.items ?? []}
        totalCounts={getCustomerUserCustomerUserList?.totalCount ?? 10}
        loading={load}
        filterControls={controls}
        components={{
          renderRightToolbarButton,
        }}
        tableColumnList={tableHeaderList}
        selectedColumnList={headerList}
        tablePreferenceLoading={tablePrefLoading}
        handleColumnSave={handleSavePreference}
      />
    </SystemUserContainer>
  );
};

export default withDynamicTableContext(SystemUsers, false, axiosInstance);
